import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useStore } from '../../contexts/StoreContext';
import { useAppNavAccess } from '../../hooks/useAppNavAccess';
import { useVisibleTools } from '../../tools/useVisibleTools';
import ToolsMenu from '../ToolsMenu/ToolsMenu';
import { usePermissions } from '../../hooks/usePermissions';
import { useAssistantDock } from '../AssistantDock/assistantDockContext';
import { useNotificationRealtime } from '../../hooks/useNotificationRealtime';
import { useChatContext } from '../../contexts/ChatContext';
import { usePrefetch } from '../../hooks/usePrefetch';
import { checkBirthdayStatus } from '../../utils/birthday.utils';
import { useAdminSidebar } from '../../contexts/AdminSidebarContext';
import Icon from '../Icon/Icon';
import NotificationBell from '../NotificationBell/NotificationBell';
import HeaderAlerts from '../Alert/HeaderAlerts';
import { isSupportAccessActive } from '../../utils/superAdminSupportAccess';
import { isCentreNoticeForOpenThread } from '../../utils/centreViewingState';
import centreService from '../../services/centre.service';
import { AppTool } from '../../tools/appTools';
import './Header.scss';

const COMMISSIONS_TOOL: AppTool = {
  id: 'my-commissions',
  label: 'Mes commissions',
  hint: 'Suivi de vos commissions',
  path: '/my-commissions',
  icon: 'dollar-sign',
  permission: 'VIEW_COMMISSIONS',
  keywords: ['commission', 'commissions', 'paiements'],
  load: () => import('../../pages/MyCommissions/MyCommissions')
};

// Context for sharing orders info with header
export const OrdersHeaderContext = React.createContext<{
  ordersHeaderInfo: {
    counter?: string;
    selectedCount?: number;
    savedSearchInfo?: {
      icon: string;
      name: string;
      resultCount: number;
      onExit: () => void;
    };
    parcelScanAction?: {
      visible: boolean;
      onOpen: () => void;
    };
    desktopHardwareParcelScanAction?: {
      visible: boolean;
      onOpen: () => void;
    };
  } | null;
  setOrdersHeaderInfo: (info: any) => void;
}>({
  ordersHeaderInfo: null,
  setOrdersHeaderInfo: () => {}
});

export const useOrdersHeader = () => React.useContext(OrdersHeaderContext);

/** Keep a fixed dropdown inside the viewport, aligned to the trigger when possible. */
const getAnchoredDropdownPosition = (buttonRect: DOMRect, preferredWidth = 280) => {
  const gutter = 8;
  const width = Math.min(preferredWidth, window.innerWidth - gutter * 2);
  let left = buttonRect.right - width;
  const maxLeft = window.innerWidth - width - gutter;
  left = Math.max(gutter, Math.min(left, maxLeft));
  return {
    top: buttonRect.bottom + gutter,
    left,
    width,
  };
};

interface PresenceUser {
  userId: number;
  firstName?: string;
  lastName?: string;
  pseudo?: string;
  /** False when user's window/tab is in background (avatar shown with reduced opacity) */
  isActive?: boolean;
}

/**
 * Header Component
 * Fixed header with logo, navigation, store selector, and user menu
 */

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { currentStore, stores, selectStore } = useStore();
  const supportMode = isSupportAccessActive();
  const { ready: navReady, canCentre, canCommissions, canAdmin, canSearchOrders } = useAppNavAccess();
  const { canTools, tools } = useVisibleTools();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const canAssistant = !permissionsLoading && hasPermission('USE_AI');
  const { open: assistantOpen, toggle: toggleAssistant } = useAssistantDock();
  const canMobileParcelScan = !!currentStore && canSearchOrders;
  const { openChatOrderId } = useChatContext();
  const { prefetchRoute, prefetchDashboardData } = usePrefetch();
  
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showStoreSelector, setShowStoreSelector] = useState(false);
  const [newNotification, setNewNotification] = useState<any>(null);
  const { ordersHeaderInfo } = useOrdersHeader();
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 769 : false);
  const [connectedUsers, setConnectedUsers] = useState<PresenceUser[]>([]);
  const [centreUnread, setCentreUnread] = useState(0);
  const adminSidebar = useAdminSidebar();

  // Track mobile state for responsive burger
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 769);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!currentStore?.id || !canCentre) {
      setCentreUnread(0);
      return;
    }
    let cancelled = false;
    const load = () => {
      centreService.getUnread(currentStore.id).then((count) => {
        if (!cancelled) setCentreUnread(count);
      }).catch(() => {});
    };
    load();
    const timer = window.setInterval(load, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [currentStore?.id, canCentre]);

  // Check if birthday is soon
  const birthdayStatus = checkBirthdayStatus(user?.birth_date);
  
  // Refs for calculating dropdown positions
  const storeButtonRef = useRef<HTMLButtonElement>(null);
  const userButtonRef = useRef<HTMLButtonElement>(null);
  
  // State for dropdown positions
  const [storeDropdownPosition, setStoreDropdownPosition] = useState({ top: 0, left: 0, width: 280 });
  const [userDropdownPosition, setUserDropdownPosition] = useState({ top: 0, right: 0 });
  
  // Calculate dropdown positions when they open
  useEffect(() => {
    if (showStoreSelector && storeButtonRef.current) {
      setStoreDropdownPosition(
        getAnchoredDropdownPosition(storeButtonRef.current.getBoundingClientRect())
      );
    }
  }, [showStoreSelector]);
  
  useEffect(() => {
    if (showUserMenu && userButtonRef.current) {
      const buttonRect = userButtonRef.current.getBoundingClientRect();
      setUserDropdownPosition({
        top: buttonRect.bottom + 8,
        right: window.innerWidth - buttonRect.right
      });
    }
  }, [showUserMenu]);
  
  // Update positions on scroll/resize
  useEffect(() => {
    const updatePositions = () => {
      if (showStoreSelector && storeButtonRef.current) {
        setStoreDropdownPosition(
          getAnchoredDropdownPosition(storeButtonRef.current.getBoundingClientRect())
        );
      }
      if (showUserMenu && userButtonRef.current) {
        const buttonRect = userButtonRef.current.getBoundingClientRect();
        setUserDropdownPosition({
          top: buttonRect.bottom + 8,
          right: window.innerWidth - buttonRect.right
        });
      }
    };
    
    window.addEventListener('scroll', updatePositions, true);
    window.addEventListener('resize', updatePositions);
    
    return () => {
      window.removeEventListener('scroll', updatePositions, true);
      window.removeEventListener('resize', updatePositions);
    };
  }, [showStoreSelector, showUserMenu]);

  // Handle real-time notification received (global - works on all pages)
  const handleNotificationReceived = useCallback((notification: any) => {
    console.log('🔔 [HEADER] New notification received:', notification);
    setNewNotification(notification); // Trigger NotificationBell update
  }, []);

  // Determine if toast should be shown for this notification
  const shouldShowToast = useCallback((notification: any) => {
    // Respect the user's in-app notification channel preference
    if (user?.preferences?.in_app_notifications === false) {
      return false;
    }

    // On mobile, skip popups to keep the interface clean.
    if (isMobile) {
      return false;
    }

    // If it's a comment notification and the chat for that order is currently open, suppress toast
    if (notification.type === 'COMMENT_ADDED' && notification.entity_type === 'order') {
      const isChatOpen =
        openChatOrderId != null &&
        Number(openChatOrderId) === Number(notification.entity_id);
      if (isChatOpen) {
        console.log('🔕 [HEADER] Suppressing toast: chat already open for order', notification.entity_id);
        return false;
      }
    }
    if (isCentreNoticeForOpenThread(notification)) {
      return false;
    }
    return true;
  }, [openChatOrderId, isMobile, user?.preferences?.in_app_notifications]);

  // Initialize global Socket.io connection for notifications
  const token = localStorage.getItem('accessToken');
  useNotificationRealtime({
    storeId: currentStore?.id || null,
    accessToken: token,
    onNotificationReceived: handleNotificationReceived,
    shouldShowToast,
    onPresenceUpdated: (data: { storeId: number; users: PresenceUser[] }) => {
      if (!currentStore?.id || data.storeId !== currentStore.id) {
        return;
      }
      setConnectedUsers(data.users || []);
    }
  });

  // Prefetch dashboard data if user is authenticated and not on dashboard
  useEffect(() => {
    prefetchDashboardData(location.pathname);
  }, [location.pathname, prefetchDashboardData]);

  useEffect(() => {
    setConnectedUsers([]);
  }, [currentStore?.id]);

  const isActive = (path: string) => location.pathname.startsWith(path);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  const handleStoreSelect = (storeId: number) => {
    const next = stores.find((store) => store.id === storeId);
    if (!next || next.id === currentStore?.id) {
      setShowStoreSelector(false);
      return;
    }
    selectStore(next.id);
    setShowStoreSelector(false);
    // Rechargement complet : le changement de boutique ne doit pas garder
    // l'état mémoire de la boutique précédente.
    window.location.assign('/');
  };

  const isAdminMobile = isMobile && location.pathname.startsWith('/admin');
  const maxPresenceAvatars = 5;
  const otherUsers = connectedUsers.filter((u) => u.userId !== user?.id);
  const visibleUsers = otherUsers.slice(0, maxPresenceAvatars);
  const extraCount = Math.max(otherUsers.length - maxPresenceAvatars, 0);

  const getPresenceInitials = (presenceUser: PresenceUser) => {
    const firstInitial = presenceUser.firstName?.[0] || '';
    const lastInitial = presenceUser.lastName?.[0] || '';
    const initials = `${firstInitial}${lastInitial}`.toUpperCase();
    if (initials) {
      return initials;
    }
    if (presenceUser.pseudo) {
      return presenceUser.pseudo[0].toUpperCase();
    }
    return 'U';
  };

  const getPresenceDisplayName = (presenceUser: PresenceUser) => {
    const fullName = `${presenceUser.firstName || ''} ${presenceUser.lastName || ''}`.trim();
    return fullName || presenceUser.pseudo || 'Utilisateur';
  };

  return (
    <header className={`header ${isAdminMobile ? 'header--admin-mobile' : ''}`}>
        <div className="header__container">
          {/* Admin burger menu - mobile only, left of logo, admin pages only */}
          {isAdminMobile && adminSidebar && (
            <button
              className="header__admin-burger"
              onClick={adminSidebar.toggleMobileSidebar}
              aria-label="Menu administration"
              title="Menu administration"
            >
              <Icon name="menu" size={20} />
            </button>
          )}

          {/* Logo */}
          <Link to="/dashboard" className="header__logo">
            <img src="/favicon.svg" alt="Zeedcom" className="header__logo-image" />
          </Link>

        {/* Navigation - Desktop */}
        <nav className={`header__nav${navReady ? ' header__nav--ready' : ''}`}>
          {navReady && (
            <>
          <Link 
            to="/dashboard" 
            className={`header__nav-link ${isActive('/dashboard') ? 'header__nav-link--active' : ''}`}
            onMouseEnter={() => prefetchRoute(() => import('../../pages/Dashboard/DashboardNew'), 'dashboard')}
          >
            Dashboard
          </Link>

          <Link 
            to="/orders" 
            className={`header__nav-link ${isActive('/orders') ? 'header__nav-link--active' : ''}`}
            onMouseEnter={() => prefetchRoute(() => import('../../pages/Orders/Orders'), 'orders')}
          >
            Commandes
          </Link>

          {canCentre && (
            <Link
              to="/centre"
              className={`header__nav-link ${isActive('/centre') ? 'header__nav-link--active' : ''}`}
              onMouseEnter={() => prefetchRoute(() => import('../../pages/Centre/Centre'), 'centre')}
            >
              Centre
              {centreUnread > 0 && (
                <span className="header__nav-badge">{centreUnread > 99 ? '99+' : centreUnread}</span>
              )}
            </Link>
          )}

          {canAdmin && (
            <Link 
              to="/admin" 
              className={`header__nav-link ${isActive('/admin') ? 'header__nav-link--active' : ''}`}
              onMouseEnter={() => prefetchRoute(() => import('../../pages/Admin/Admin'), 'admin')}
            >
              Admin
            </Link>
          )}

          {(canTools || canCommissions) && !isMobile && (
            <ToolsMenu
              tools={canCommissions ? [...tools, COMMISSIONS_TOOL] : tools}
              active={isActive('/outils') || isActive('/my-commissions')}
            />
          )}
            </>
          )}
        </nav>

        {/* Connected users - après le menu (desktop only) */}
        {!isMobile && otherUsers.length > 0 && (
          <div className="header__presence" aria-label="Utilisateurs connectés">
            <div className="header__presence-list">
              {visibleUsers.map((presenceUser) => (
                <div
                  key={presenceUser.userId}
                  className={`header__presence-avatar${presenceUser.isActive === false ? ' header__presence-avatar--inactive' : ''}`}
                  title={getPresenceDisplayName(presenceUser)}
                >
                  {getPresenceInitials(presenceUser)}
                </div>
              ))}
              {extraCount > 0 && (
                <div
                  className="header__presence-more"
                  title={`${extraCount} autre${extraCount > 1 ? 's' : ''}`}
                >
                  +{extraCount}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Right Section - Store | Cloche | Profil */}
        <div className="header__right">
          {/* Store Selector */}
          {stores.length > 1 && currentStore && !supportMode && (
            <div className="header__store-selector">
              <button
                ref={storeButtonRef}
                className="header__store-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowStoreSelector(!showStoreSelector);
                }}
              >
                <span className="header__store-name" title={currentStore.name}>
                  {currentStore.name}
                </span>
                <svg 
                  className="header__store-icon" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 16 16" 
                  fill="currentColor"
                >
                  <path d="M4 6l4 4 4-4H4z"/>
                </svg>
              </button>

              {/* Store dropdown rendered via Portal */}
            </div>
          )}

          {/* Scan colis (mobile) — toujours visible, même hors onglet Commandes */}
          {isMobile && canMobileParcelScan && (
            <button
              type="button"
              className="header__mobile-parcel-scan-btn"
              onClick={() => {
                if (
                  location.pathname.startsWith('/orders') &&
                  ordersHeaderInfo?.parcelScanAction?.onOpen
                ) {
                  ordersHeaderInfo.parcelScanAction.onOpen();
                } else {
                  navigate('/orders', { state: { openParcelScan: true } });
                }
              }}
              title="Scanner un colis"
              aria-label="Scanner un colis"
            >
              <Icon name="qrcode" size={16} />
            </button>
          )}

          {!isMobile && ordersHeaderInfo?.desktopHardwareParcelScanAction?.onOpen && (
            <button
              type="button"
              className="header__desktop-hardware-scan-btn"
              onClick={() => {
                if (
                  location.pathname.startsWith('/orders') &&
                  ordersHeaderInfo.desktopHardwareParcelScanAction?.onOpen
                ) {
                  ordersHeaderInfo.desktopHardwareParcelScanAction.onOpen();
                } else {
                  navigate('/orders', { state: { openParcelHardwareScan: true } });
                }
              }}
              title="Scan colis — lecteur USB ou Bluetooth"
              aria-label="Scan colis — lecteur USB ou Bluetooth"
            >
              <Icon name="barcode" size={17} />
            </button>
          )}

          {/* Mobile Search Button - Only visible on mobile */}
          <button
            className="header__mobile-search-btn"
            onClick={() => navigate('/orders?search=true')}
            title="Rechercher des commandes"
          >
            <Icon name="search" size={16} />
          </button>

          {canAssistant && (
            <button
              type="button"
              className={`header__assistant-btn${assistantOpen ? ' is-on' : ''}`}
              onClick={toggleAssistant}
              title="Assistant général"
              aria-label="Assistant général"
              aria-pressed={assistantOpen}
            >
              <Icon name="sparkles" size={15} />
            </button>
          )}

          {!isMobile && (location.pathname.startsWith('/orders') || location.pathname.startsWith('/dashboard')) && (
            <HeaderAlerts />
          )}

          {/* Notifications */}
          <NotificationBell newNotification={newNotification} />

          {/* User Menu - Profil (desktop + mobile) */}
          <div className="header__user-menu">
            <button
              ref={userButtonRef}
              className="header__user-button"
              onClick={() => setShowUserMenu(!showUserMenu)}
              title={`${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() || 'Profil'}
            >
              <div className="header__user-avatar">
                {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Overlay for closing dropdowns - Render via Portal */}
      {(showUserMenu || showStoreSelector) && createPortal(
        <div 
          className="header__overlay"
          onClick={(e) => {
            // Only close if clicking directly on overlay, not on dropdowns
            if (e.target === e.currentTarget) {
              setShowUserMenu(false);
              setShowStoreSelector(false);
            }
          }}
        />,
        document.body
      )}
      
      {/* Store Dropdown Portal */}
      {showStoreSelector && !supportMode && createPortal(
        <div 
          className="header__store-dropdown"
          style={{ 
            position: 'fixed',
            top: `${storeDropdownPosition.top}px`,
            left: `${storeDropdownPosition.left}px`,
            width: `${storeDropdownPosition.width}px`,
            zIndex: 1200,
            display: 'block',
            visibility: 'visible',
            opacity: 1
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
        >
          {stores.map(store => (
            <button
              key={store.id}
              type="button"
              className={`header__store-item ${store.id === currentStore?.id ? 'header__store-item--active' : ''}`}
              title={store.name}
              onClick={(e) => {
                e.stopPropagation();
                handleStoreSelect(store.id);
              }}
            >
              <span className="header__store-item-name">{store.name}</span>
              {store.roles && store.roles.length > 0 && (
                <span className="header__store-item-role">
                  {store.roles[0]}
                </span>
              )}
            </button>
          ))}
          
          <Link 
            to="/stores" 
            className="header__store-item header__store-item--all"
            onClick={(e) => {
              e.stopPropagation();
              setShowStoreSelector(false);
            }}
          >
            Tous les stores
          </Link>
        </div>,
        document.body
      )}
      
      {/* User Dropdown Portal */}
      {showUserMenu && createPortal(
        <div 
          className="header__user-dropdown"
          style={{ 
            position: 'fixed',
            top: `${userDropdownPosition.top}px`,
            right: `${userDropdownPosition.right}px`,
            zIndex: 1200,
            display: 'block',
            visibility: 'visible',
            opacity: 1
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
        >
          {/* Connected users - mobile only, above user name */}
          {isMobile && otherUsers.length > 0 && (
            <div className="header__user-dropdown-presence">
              <div className="header__user-dropdown-presence-list">
                {visibleUsers.map((presenceUser) => (
                  <div
                    key={presenceUser.userId}
                    className={`header__user-dropdown-presence-avatar${presenceUser.isActive === false ? ' header__user-dropdown-presence-avatar--inactive' : ''}`}
                    title={getPresenceDisplayName(presenceUser)}
                  >
                    {getPresenceInitials(presenceUser)}
                  </div>
                ))}
                {extraCount > 0 && (
                  <div
                    className="header__user-dropdown-presence-more"
                    title={`${extraCount} autre${extraCount > 1 ? 's' : ''}`}
                  >
                    +{extraCount}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="header__user-info">
            <div className="header__user-info-name">
              {user?.first_name} {user?.last_name}
            </div>
            <div className="header__user-info-email">
              {user?.email}
            </div>
          </div>

          <div className="header__user-divider"></div>

          <Link 
            to="/profile" 
            className="header__user-menu-item"
            onClick={() => setShowUserMenu(false)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 8a3 3 0 100-6 3 3 0 000 6zM8 9c-3.866 0-7 2.239-7 5v1h14v-1c0-2.761-3.134-5-7-5z"/>
            </svg>
            Profil
          </Link>

          <Link 
            to="/preferences" 
            className="header__user-menu-item"
            onClick={() => setShowUserMenu(false)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 4.754a3.246 3.246 0 100 6.492 3.246 3.246 0 000-6.492zM5.754 8a2.246 2.246 0 114.492 0 2.246 2.246 0 01-4.492 0z"/>
              <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 01-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 01-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 01.52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 011.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 011.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 01.52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 01-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 01-1.255-.52l-.094-.319z"/>
            </svg>
            Préférences
          </Link>

          <Link 
            to="/help" 
            className="header__user-menu-item"
            onClick={() => setShowUserMenu(false)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 15A7 7 0 118 1a7 7 0 010 14zm0 1A8 8 0 108 0a8 8 0 000 16z"/>
              <path d="M5.255 5.786a.237.237 0 00.241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 00.25.246h.811a.25.25 0 00.25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z"/>
            </svg>
            Aide
          </Link>

          {/* Birthday Option - Only show if birthday is within 7 days or today */}
          {birthdayStatus.isBirthdaySoon && (
            <>
              <div className="header__user-divider"></div>
              <Link 
                to="/birthday" 
                className="header__user-menu-item header__user-menu-item--birthday"
                onClick={() => setShowUserMenu(false)}
              >
                <span className="header__birthday-icon">🎉</span>
                <span className="header__birthday-text">
                  {birthdayStatus.isToday ? '🎂 Joyeux Anniversaire !' : `Anniversaire dans ${birthdayStatus.daysUntilBirthday} jour${birthdayStatus.daysUntilBirthday > 1 ? 's' : ''}`}
                </span>
                <span className="header__birthday-sparkle">✨</span>
              </Link>
            </>
          )}

          <div className="header__user-divider"></div>

          <button 
            className="header__user-menu-item header__user-menu-item--danger"
            onClick={handleLogout}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path fillRule="evenodd" d="M10 12.5a.5.5 0 01-.5.5h-8a.5.5 0 01-.5-.5v-9a.5.5 0 01.5-.5h8a.5.5 0 01.5.5v2a.5.5 0 001 0v-2A1.5 1.5 0 009.5 2h-8A1.5 1.5 0 000 3.5v9A1.5 1.5 0 001.5 14h8a1.5 1.5 0 001.5-1.5v-2a.5.5 0 00-1 0v2z"/>
              <path fillRule="evenodd" d="M15.854 8.354a.5.5 0 000-.708l-3-3a.5.5 0 00-.708.708L14.293 7.5H5.5a.5.5 0 000 1h8.793l-2.147 2.146a.5.5 0 00.708.708l3-3z"/>
            </svg>
            Déconnexion
          </button>
        </div>,
        document.body
      )}
      </header>
  );
};

export default Header;
